import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { isVersionConflict } from "../../lib/api";
import { apiError, formatAmount, formatDay } from "../../lib/finance";
import { claimState } from "../../lib/claims";
import { ReceiptPreviewDialog, receiptSource } from "./ReceiptPreview";
import DeleteWithReasonDialog from "../DeleteWithReasonDialog";
import { AdvanceLine } from "./AdvancesPanel";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Skeleton } from "../ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, FileImage, Loader2, Plus, Receipt, RefreshCw } from "lucide-react";

const TONES = {
  muted: "bg-slate-100 text-slate-700", info: "bg-jamora-50 text-jamora", warning: "bg-amber-50 text-amber-900",
  success: "bg-emerald-50 text-emerald-800", danger: "bg-red-50 text-red-800",
};

// The category cap this claim exceeds: signalled to the approver, never blocking.
function OverCap({ claim, long = false }) {
  if (!claim.over_cap) return null;
  const cap = formatAmount(claim.over_cap.cap, claim.over_cap.currency);
  return long ? (
    <p className="flex items-start gap-1.5 text-sm text-amber-900 rounded-md bg-amber-50 p-3" data-testid="claim-over-cap">
      <AlertTriangle size={15} className="shrink-0 mt-0.5" />Au-dessus du plafond de la catégorie ({cap} par note). À vérifier, sans blocage.
    </p>
  ) : <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap bg-amber-50 text-amber-900" data-testid={`claim-over-cap-${claim.id}`}>Plafond {cap} dépassé</span>;
}

export function ClaimBadge({ claim }) {
  const s = claimState(claim);
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${TONES[s.tone]}`} data-testid={`claim-state-${claim.id}`}>{s.label}</span>;
}

function ClaimRow({ claim, onOpen, showClaimant }) {
  return (
    <button type="button" onClick={() => onOpen(claim.id)} className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`claim-row-${claim.id}`}>
      <Receipt size={18} className="text-slate-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-slate-900 truncate">{claim.title}</div>
        <div className="text-xs text-slate-500 truncate">
          {formatDay(claim.incurred_at)}{showClaimant ? ` · ${claim.claimant.name}` : ""}{claim.mission ? ` · ${claim.mission.reference}` : ""}
        </div>
      </div>
      <div className="text-right shrink-0 space-y-1">
        <div className="text-sm font-semibold text-slate-900">{formatAmount(claim.amount, claim.currency)}</div>
        <div className="flex flex-wrap justify-end gap-1">{showClaimant && <OverCap claim={claim} />}<ClaimBadge claim={claim} /></div>
      </div>
    </button>
  );
}

// « Mes frais »: one's own notes de frais, what is still owed, and the way to declare a new one.
export function MyClaims() {
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading", data: null, error: "" });
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/claims/mine");
      setState({ phase: "ready", data, error: "" });
    } catch (e) {
      setState({ phase: "error", data: null, error: apiError(e) });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const data = state.data;
  // Shown when something is still owed to the person, or when they hold an advance (14-3).
  const balances = Object.entries(data?.balance || {}).filter(([, b]) => Number(b.remaining) > 0 || Number(b.advanced) > 0);
  return (
    <div className="space-y-3" data-testid="claims-mine">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-slate-500">Ce que vous avez payé pour Jamora : envoyez le reçu, suivez l'approbation et le remboursement.</p>
        <Button onClick={() => navigate("/finance/frais/nouvelle")}  disabled={data && !data.has_fiche} data-testid="claims-new">
          <Plus size={16} className="mr-1.5" />Nouvelle note de frais
        </Button>
      </div>
      {state.phase === "loading" && <Skeleton className="h-24 w-full" />}
      {state.phase === "error" && <Card className="p-4 text-sm text-slate-600 flex items-center gap-2">{state.error}<Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button></Card>}
      {data && !data.has_fiche && (
        <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="claims-no-fiche">
          Votre compte n'est lié à aucune fiche collaborateur : demandez à un administrateur de la lier pour déclarer vos frais.
        </Card>
      )}
      {balances.length > 0 && (
        <Card className="p-4 space-y-3" data-testid="claims-balance">
          {balances.map(([c, b]) => (
            <div key={c} className="flex flex-wrap gap-x-6 gap-y-1">
              {Number(b.to_return) > 0 ? (
                <div><div className="text-xs text-slate-500">Avance à restituer</div><div className="text-lg font-semibold text-amber-900 tabular-nums">{formatAmount(b.to_return, c)}</div></div>
              ) : (
                <div><div className="text-xs text-slate-500">Reste à vous rembourser</div><div className="text-lg font-semibold text-slate-900 tabular-nums">{formatAmount(b.remaining, c)}</div></div>
              )}
              {Number(b.advanced) > 0 && <div><div className="text-xs text-slate-500">Avances reçues</div><div className="text-sm text-slate-800 tabular-nums pt-1">{formatAmount(b.advanced, c)}</div></div>}
              {Number(b.advanced) > 0 && <div><div className="text-xs text-slate-500">Frais approuvés</div><div className="text-sm text-slate-800 tabular-nums pt-1">{formatAmount(b.owed, c)}</div></div>}
            </div>
          ))}
          {Object.values(data.balance).some((b) => Number(b.advanced) > 0) && (
            <p className="text-xs text-slate-500">Vos frais approuvés sont d'abord pris sur l'avance reçue ; le reste vous est remboursé, ou ce qui n'a pas été dépensé est à rendre.</p>
          )}
        </Card>
      )}
      {data && data.items.length === 0 && data.has_fiche && (
        <Card className="p-8 text-center space-y-2">
          <Receipt className="mx-auto text-slate-300" size={24} />
          <p className="text-sm text-slate-600">Aucune note de frais pour le moment.</p>
        </Card>
      )}
      {data && data.items.length > 0 && (
        <Card className="divide-y divide-slate-100 overflow-hidden">
          {data.items.map((c) => <ClaimRow key={c.id} claim={c} onOpen={setOpenId} />)}
        </Card>
      )}
      {data?.advances?.length > 0 && (
        <Card className="overflow-hidden">
          <div className="px-4 pt-3 text-xs font-medium text-slate-500">Avances de mission</div>
          <ul className="divide-y divide-slate-100" data-testid="claims-advances">{data.advances.map((a) => <AdvanceLine key={a.id} a={a} />)}</ul>
        </Card>
      )}
      <ClaimSheet claimId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}

// « À approuver »: the queue of notes de frais waiting for a decision.
export function ClaimsReview({ onCount }) {
  const [state, setState] = useState({ phase: "loading", items: [], error: "" });
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/claims");
      setState({ phase: "ready", items: data.items, error: "" });
      onCount?.(data.items.filter((c) => !c.own).length);
    } catch (e) {
      setState({ phase: "error", items: [], error: apiError(e) });
    }
  }, [onCount]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="claims-review">
      <p className="text-sm text-slate-500">Approuver une note de frais la rend « à rembourser » dans la Finance. Vous ne voyez pas les boutons sur vos propres frais.</p>
      {state.phase === "loading" && <Skeleton className="h-24 w-full" />}
      {state.phase === "error" && <Card className="p-4 text-sm text-slate-600">{state.error}</Card>}
      {state.phase === "ready" && state.items.length === 0 && (
        <Card className="p-8 text-center text-sm text-slate-600" data-testid="claims-review-empty">Aucune note de frais en attente.</Card>
      )}
      {state.items.length > 0 && (
        <Card className="divide-y divide-slate-100 overflow-hidden">
          {state.items.map((c) => <ClaimRow key={c.id} claim={c} onOpen={setOpenId} showClaimant />)}
        </Card>
      )}
      <ClaimSheet claimId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}

const HISTORY = { claim_created: "Créée", claim_corrected: "Corrigée", claim_submitted: "Envoyée pour approbation", claim_approved: "Approuvée",
  claim_rejected: "Refusée", claim_to_correct: "Renvoyée à corriger" };

export function ClaimSheet({ claimId, onClose, onChanged }) {
  const [claim, setClaim] = useState(null);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null); // reject | request_changes
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    if (!claimId) return;
    setClaim(null);
    try {
      const { data } = await api.get(`/finance/claims/${claimId}`);
      setClaim(data);
      setError("");
    } catch (e) {
      setError(apiError(e));
    }
  }, [claimId]);
  useEffect(() => { load(); }, [load]);

  const act = async (action, reason) => {
    setBusy(action);
    try {
      const path = action === "request_changes" ? "request-changes" : action;
      const { data } = await api.post(`/finance/claims/${claim.id}/${path}`, { expected_version: claim.version, ...(reason ? { reason } : {}) });
      setClaim(data);
      setDialog(null);
      toast.success({ approve: "Note de frais approuvée", reject: "Note de frais refusée", request_changes: "Note renvoyée à corriger", submit: "Note de frais envoyée" }[action]);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
      if (isVersionConflict(e)) load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open={!!claimId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="claim-sheet">
        <SheetHeader><SheetTitle className="pr-6">{claim?.title || "Note de frais"}</SheetTitle></SheetHeader>
        {error && <p className="text-sm text-red-700 py-4">{error}</p>}
        {!claim && !error && <Skeleton className="h-48 w-full mt-4" />}
        {claim && (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between gap-2">
              <span className="text-2xl font-semibold text-slate-900" data-testid="claim-amount">{formatAmount(claim.amount, claim.currency)}</span>
              <ClaimBadge claim={claim} />
            </div>
            <OverCap claim={claim} long />
            {claim.review?.reason && ["to_correct", "rejected"].includes(claim.review.status) && (
              <p className="flex items-start gap-1.5 text-sm text-amber-900 rounded-md bg-amber-50 p-3" data-testid="claim-review-reason">
                <AlertTriangle size={15} className="shrink-0 mt-0.5" />{claim.review.status === "rejected" ? "Refusée" : "À corriger"} : {claim.review.reason}
              </p>
            )}
            <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-y-2 text-sm">
              <dt className="text-slate-500">Pour</dt><dd className="text-slate-800">{claim.claimant.name}</dd>
              <dt className="text-slate-500">Date</dt><dd className="text-slate-800">{formatDay(claim.incurred_at)}</dd>
              <dt className="text-slate-500">Catégorie</dt><dd className="text-slate-800">{claim.category_name}</dd>
              {claim.supplier && <><dt className="text-slate-500">Fournisseur</dt><dd className="text-slate-800 break-words">{claim.supplier}</dd></>}
              {claim.mission && <><dt className="text-slate-500">Mission</dt><dd><Link to={`/missions/${claim.mission.id}`} className="text-jamora hover:underline">{claim.mission.reference} · {claim.mission.title}</Link></dd></>}
              {claim.reimbursed && <><dt className="text-slate-500">Déjà réglé</dt><dd className="text-slate-800">{formatAmount(claim.reimbursed, claim.currency)}</dd></>}
              {claim.review?.by_name && <><dt className="text-slate-500">Décision</dt><dd className="text-slate-800">{claim.review.by_name}, le {formatDay(claim.review.at?.slice(0, 10))}</dd></>}
            </dl>
            {claim.description && <p className="text-sm text-slate-700 whitespace-pre-line">{claim.description}</p>}
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-slate-500">Reçu</div>
              {claim.attachments.map((a) => (
                <button key={a.id} type="button" disabled={a.unavailable} onClick={() => setPreview({ source: receiptSource.attachment(a.id), name: a.name })}
                  className="flex items-center gap-2 text-sm text-jamora hover:underline disabled:text-slate-400" data-testid={`claim-receipt-${a.id}`}>
                  <FileImage size={15} />{a.unavailable ? "Reçu indisponible" : a.name}
                </button>
              ))}
              {claim.attachments.length === 0 && <p className="text-sm text-amber-800">Aucun reçu.</p>}
            </div>
            <ol className="space-y-1 border-t border-slate-100 pt-3">
              {claim.history.map((h) => <li key={`${h.kind}-${h.at}`} className="text-xs text-slate-500">{HISTORY[h.kind] || h.kind} · {formatDay(h.at.slice(0, 10))} · {h.actor_name}</li>)}
            </ol>
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
              {claim.capabilities.delete && (
                <Button variant="outline" className="text-red-700 sm:mr-auto" onClick={() => setDeleting(claim)} data-testid="claim-delete">Supprimer…</Button>
              )}
              {claim.capabilities.edit && (
                <>
                  <Button variant="outline" asChild><Link to={`/finance/frais/${claim.id}/modifier`} data-testid="claim-edit">Corriger</Link></Button>
                  <Button onClick={() => act("submit")} disabled={!!busy || claim.attachments.length === 0}  data-testid="claim-submit">
                    {busy === "submit" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Envoyer pour approbation
                  </Button>
                </>
              )}
              {claim.capabilities.review && (
                <>
                  <Button onClick={() => act("approve")} disabled={!!busy}  data-testid="claim-approve">
                    {busy === "approve" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Approuver
                  </Button>
                  <Button variant="outline" onClick={() => setDialog("request_changes")} disabled={!!busy} data-testid="claim-request-changes">À corriger</Button>
                  <Button variant="outline" onClick={() => setDialog("reject")} disabled={!!busy} className="text-red-700" data-testid="claim-reject">Refuser</Button>
                </>
              )}
            </div>
          </div>
        )}
        <ReasonDialog kind={dialog} onClose={() => setDialog(null)} busy={!!busy} onConfirm={(reason) => act(dialog, reason)} />
        <DeleteWithReasonDialog
          target={deleting} testId="delete-claim" onClose={() => setDeleting(null)} confirmLabel="Supprimer définitivement"
          title={`Supprimer « ${deleting?.title || ""} » ?`}
          description="Ce brouillon n'a jamais été envoyé : il est supprimé pour de bon, avec son motif gardé dans l'historique. Son reçu est conservé."
          onConfirm={async (reason) => {
            const target = deleting;
            try {
              await api.delete(`/finance/claims/${target.id}`, { data: { reason } });
              toast.success("Brouillon supprimé");
              setDeleting(null);
              onChanged?.();
              onClose();
            } catch (e) {
              setDeleting(null);
              toast.error(apiError(e));
            }
          }}
        />
        <ReceiptPreviewDialog receipt={preview} onClose={() => setPreview(null)} description="Reçu de la note de frais." />
      </SheetContent>
    </Sheet>
  );
}

function ReasonDialog({ kind, onClose, busy, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (kind) setReason(""); }, [kind]);
  const reject = kind === "reject";
  return (
    <Dialog open={!!kind} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent data-testid="claim-reason-dialog">
        <DialogHeader>
          <DialogTitle>{reject ? "Refuser la note de frais ?" : "Renvoyer à corriger ?"}</DialogTitle>
          <DialogDescription>{reject ? "Elle ne sera pas remboursée et ne comptera pas dans les dépenses. Le motif est montré à la personne." : "La personne pourra la corriger puis la renvoyer."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="claim-reason">Motif *</Label>
          <Textarea id="claim-reason" rows={3} maxLength={500} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="claim-reason" />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={() => onConfirm(reason.trim())} disabled={busy || reason.trim().length < 3} data-testid="claim-reason-confirm"
            className={reject ? "bg-red-600 hover:bg-red-700 text-white" : undefined}>
            {reject ? "Refuser" : "Renvoyer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
